import { GraphQLFormattedError } from 'graphql';
import { rgba } from 'polished';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Callout } from './Callout';

type GraphQLErrorExtensions = {
	extensions: { error: { key: string; data: { details: IValidationErrorDetail[] } } };
};

interface IProps {
	error?: GraphQLFormattedError;
	warning?: boolean;
	showValidationDetails?: boolean;
	overrideFieldNames?: {
		[key: string]: string;
	};
}

interface IValidationErrorDetail {
	context: {
		label: string;
		key: string;
	};
	message: string;
	path: (string | number)[];
	type: string;
}

const Element = styled.p`
	color: ${(props) => props.theme.colors.danger};
	background: ${(props) => rgba(props.theme.colors.danger, 0.1)};
	padding: 8px;
	border-radius: 4px;
	margin: 10px 0;
`;

const ValidationErrorsElement = styled.ul`
	padding-left: 1.5em;
	margin-block-end: 0;
`;

const hasGraphQLErrorExtensions = (
	error: GraphQLFormattedError,
): error is GraphQLFormattedError & GraphQLErrorExtensions => {
	if (!error.extensions || !error.extensions.error) {
		return false;
	}
	return true;
};

function ValidationErrors(props: {
	error?: GraphQLFormattedError;
	showValidationDetails?: boolean;
	overrideFieldNames?: {
		[key: string]: string;
	};
}) {
	const { t } = useTranslation();
	const { error, showValidationDetails, overrideFieldNames } = props;

	if (!error || !hasGraphQLErrorExtensions(error)) {
		return undefined;
	}

	const { key, data } = error?.extensions?.error ?? {};

	if (key !== 'errors.validation.validationError' || !showValidationDetails) {
		return null;
	}

	return (
		<ValidationErrorsElement>
			{data.details?.map((detail: IValidationErrorDetail, index: number) => {
				const field = overrideFieldNames?.[detail.context.key] ?? detail.context.key;

				return <li key={index}>{t(`errors.validation.rules.${detail.type}`, { field })}</li>;
			})}
		</ValidationErrorsElement>
	);
}

export function GraphQLErrorMessage(props: IProps) {
	const { t } = useTranslation();
	const { error, warning } = props;

	if (!error || !hasGraphQLErrorExtensions(error)) {
		return undefined;
	}

	const { key, data } = error?.extensions?.error ?? { key: undefined, data: undefined };

	if (warning) {
		return (
			<Callout color="yellow" mb={3} description={t(key, data)}>
				<ValidationErrors {...props} />
			</Callout>
		);
	}

	return (
		<Element>
			{t(key, data)}
			<ValidationErrors {...props}></ValidationErrors>
		</Element>
	);
}
